/**
 * Pure admission decision for a registered SIP engine.
 *
 * This module deliberately has no database or runtime imports. Callers still
 * own profile/assignment checks; this only decides whether the registry row is
 * safe to use as a live runtime.
 */

export type RuntimeAdmissionEvidence = 'explicit-runtime' | 'unavailable';

export type RuntimeAdmissionReason =
  | 'EXPLICIT_RUNTIME_READY'
  | 'SERVICE_TYPE_MISMATCH'
  | 'STATUS_NOT_HEALTHY'
  | 'PORT_NOT_LISTENING'
  | 'HEARTBEAT_MISSING'
  | 'HEARTBEAT_INVALID'
  | 'HEARTBEAT_FUTURE'
  | 'HEARTBEAT_STALE'
  | 'RUNTIME_STATE_MISSING'
  | 'RUNTIME_WAITING'
  | 'RUNTIME_STATE_UNKNOWN'
  | 'RUNTIME_IDENTITY_INVALID'
  | 'GENERATION_MISMATCH'
  | 'PUBLICATION_SEQUENCE_MISMATCH'
  | 'PUBLICATION_MISSING'
  | 'PUBLICATION_INVALID'
  | 'PUBLICATION_FUTURE'
  | 'PUBLICATION_STALE';

export interface RuntimeAdmissionInput {
  readonly serviceType?: unknown;
  readonly status?: unknown;
  readonly port?: unknown;
  readonly lastHeartbeat?: unknown;
  readonly containerData?: unknown;
  readonly now?: Date;
  /** Existing caller policy. The default matches current engine discovery. */
  readonly maxHeartbeatAgeMs?: number;
  readonly expectedGeneration?: number;
  readonly expectedPublicationSequence?: number;
}

export interface RuntimeAdmissionResult {
  readonly eligible: boolean;
  /** Readiness is always explicit under the current runtime contract. */
  readonly runtimeReady: boolean;
  readonly evidence: RuntimeAdmissionEvidence;
  readonly reason: RuntimeAdmissionReason;
}

export const DEFAULT_RUNTIME_ADMISSION_HEARTBEAT_AGE_MS = 120_000;
export const RUNTIME_ADMISSION_FUTURE_SKEW_MS = 30_000;
export const RUNTIME_ADMISSION_MIN_PUBLICATION_INTERVAL_MS = 1_000;
export const RUNTIME_ADMISSION_MAX_PUBLICATION_INTERVAL_MS = 300_000;
export const RUNTIME_ADMISSION_MAX_PUBLICATION_AGE_MS = 300_000;
export const RUNTIME_ADMISSION_DEFAULT_PUBLICATION_INTERVAL_MS = 10_000;

const WAITING_STATES = new Set(['waiting-for-configuration', 'starting', 'degraded', 'configerror', 'stopping']);
const RUNTIME_STATES = new Set(['ready']);

function result(
  eligible: boolean,
  runtimeReady: boolean,
  evidence: RuntimeAdmissionEvidence,
  reason: RuntimeAdmissionReason,
): RuntimeAdmissionResult {
  return { eligible, runtimeReady, evidence, reason };
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function finiteInteger(value: unknown): number | null {
  const number = finiteNumber(value);
  return number !== null && Number.isSafeInteger(number) ? number : null;
}

function parseTimestamp(value: unknown): number | null {
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }
  if (typeof value !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?(Z|[+-]\d{2}:\d{2})$/.exec(value);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  if (month < 1 || month > 12 || day < 1 || day > new Date(Date.UTC(year, month, 0)).getUTCDate()
    || hour > 23 || minute > 59 || second > 59) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function heartbeatDecision(input: RuntimeAdmissionInput, now: number): RuntimeAdmissionResult | undefined {
  const heartbeat = parseTimestamp(input.lastHeartbeat);
  if (input.lastHeartbeat === undefined || input.lastHeartbeat === null) {
    return result(false, false, 'unavailable', 'HEARTBEAT_MISSING');
  }
  if (heartbeat === null) return result(false, false, 'unavailable', 'HEARTBEAT_INVALID');
  if (heartbeat > now + RUNTIME_ADMISSION_FUTURE_SKEW_MS) {
    return result(false, false, 'unavailable', 'HEARTBEAT_FUTURE');
  }
  const maxAge = input.maxHeartbeatAgeMs === undefined
    ? DEFAULT_RUNTIME_ADMISSION_HEARTBEAT_AGE_MS
    : finiteNumber(input.maxHeartbeatAgeMs);
  if (maxAge === null || maxAge < 0 || now - heartbeat > maxAge) {
    return result(false, false, 'unavailable', 'HEARTBEAT_STALE');
  }
  return undefined;
}

export function evaluateRuntimeAdmission(input: RuntimeAdmissionInput): RuntimeAdmissionResult {
  if (input.serviceType !== undefined && input.serviceType !== 'SipEngine') {
    return result(false, false, 'unavailable', 'SERVICE_TYPE_MISMATCH');
  }
  if (input.status !== 'healthy') return result(false, false, 'unavailable', 'STATUS_NOT_HEALTHY');
  const port = finiteInteger(input.port);
  if (port === null || port < 1 || port > 65_535) {
    return result(false, false, 'unavailable', 'PORT_NOT_LISTENING');
  }

  const now = (input.now ?? new Date()).getTime();
  if (!Number.isFinite(now)) return result(false, false, 'unavailable', 'HEARTBEAT_INVALID');
  const heartbeatFailure = heartbeatDecision(input, now);
  if (heartbeatFailure !== undefined) return heartbeatFailure;

  const data = record(input.containerData);
  const runtimeStateValue = data?.runtimeState;
  const runtimeState = typeof runtimeStateValue === 'string' ? runtimeStateValue : undefined;
  if (runtimeState === undefined) {
    return result(false, false, 'unavailable', 'RUNTIME_STATE_MISSING');
  }
  if (WAITING_STATES.has(runtimeState)) {
    return result(false, false, 'explicit-runtime', 'RUNTIME_WAITING');
  }
  if (!RUNTIME_STATES.has(runtimeState)) {
    return result(false, false, 'explicit-runtime', 'RUNTIME_STATE_UNKNOWN');
  }

  const identity = record(data?.runtimeIdentity);
  if (identity === null) {
    return result(false, false, 'explicit-runtime', 'RUNTIME_IDENTITY_INVALID');
  }

  const runtimeInstanceId = identity.runtimeInstanceId;
  const generation = finiteInteger(identity.generation);
  const sequence = finiteInteger(identity.publicationSequence);
  const publicationAt = parseTimestamp(identity.publicationAt);
  if (typeof runtimeInstanceId !== 'string' || runtimeInstanceId.length === 0
    || generation === null || generation < 1 || sequence === null || sequence < 1
    || publicationAt === null) {
    return result(false, false, 'explicit-runtime', 'RUNTIME_IDENTITY_INVALID');
  }
  if (input.expectedGeneration !== undefined && generation !== input.expectedGeneration) {
    return result(false, false, 'explicit-runtime', 'GENERATION_MISMATCH');
  }
  if (input.expectedPublicationSequence !== undefined && sequence !== input.expectedPublicationSequence) {
    return result(false, false, 'explicit-runtime', 'PUBLICATION_SEQUENCE_MISMATCH');
  }
  if (publicationAt > now + RUNTIME_ADMISSION_FUTURE_SKEW_MS) {
    return result(false, false, 'explicit-runtime', 'PUBLICATION_FUTURE');
  }
  const interval = finiteInteger(identity.publicationIntervalMs);
  if (interval === null || interval < 1) {
    return result(false, false, 'explicit-runtime', 'RUNTIME_IDENTITY_INVALID');
  }
  const boundedInterval = identity.publicationIntervalMs === undefined
    ? RUNTIME_ADMISSION_DEFAULT_PUBLICATION_INTERVAL_MS
    : Math.min(Math.max(interval ?? RUNTIME_ADMISSION_MIN_PUBLICATION_INTERVAL_MS, RUNTIME_ADMISSION_MIN_PUBLICATION_INTERVAL_MS), RUNTIME_ADMISSION_MAX_PUBLICATION_INTERVAL_MS);
  if (now - publicationAt > Math.min(boundedInterval * 3, RUNTIME_ADMISSION_MAX_PUBLICATION_AGE_MS)) {
    return result(false, false, 'explicit-runtime', 'PUBLICATION_STALE');
  }
  return result(true, true, 'explicit-runtime', 'EXPLICIT_RUNTIME_READY');
}
